import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Input,
  InputSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError, MatHint } from '@angular/material/form-field';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle } from '@angular/material/list';
import { MatRadioButton } from '@angular/material/radio';
import { getSelectionListOptionKey, isMultiSelectionList, SelectionListInputModel, SelectionListValue } from '@libs/form/models';
import { ErrorDirective, InputValidatorsDirective } from '../../../directives';
import { InputBaseComponent } from '../../../models';
import { FormDescriptionPanelComponent, FormDescriptionToggleComponent } from '../../description';
import { InputProviderFactory } from '../ng-input-provider';
import { SelectionListItemModel } from './selection-list-item.model';

export type SelectionListAppearance = 'default' | 'flat';

@Component({
  selector: 'ui-form-selection-list',
  templateUrl: './selection-list.component.html',
  styleUrls: ['./selection-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ...InputProviderFactory.getProviders(SelectionListComponent),
  ],
  imports: [
    ErrorDirective,
    FormDescriptionPanelComponent,
    FormDescriptionToggleComponent,
    InputValidatorsDirective,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatCheckbox,
    MatError,
    MatHint,
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemTitle,
    MatRadioButton,
  ],
})
export class SelectionListComponent extends InputBaseComponent implements OnInit {
  private readonly model: WritableSignal<SelectionListInputModel | undefined> = signal(undefined);
  private readonly selectedKeys: WritableSignal<string[]> = signal([]);

  public readonly appearance: InputSignal<SelectionListAppearance> = input<SelectionListAppearance>('default');

  public readonly disabled: WritableSignal<boolean> = signal(false);

  public readonly required: Signal<boolean> = computed(() => this.model()?.attributes?.required ?? false);

  public valueAccessor!: never;

  /** the description toggle in the header opens and closes the panel above the list */
  public readonly descriptionOpen: WritableSignal<boolean> = signal(false);

  /** the list renders checkboxes when it selects multiple options and radio buttons when it selects one */
  public readonly multiple: Signal<boolean> = computed(() => {
    const data: SelectionListInputModel | undefined = this.model();

    return data !== undefined && isMultiSelectionList(data);
  });

  public readonly items: Signal<SelectionListItemModel[]> = computed(() => {
    const selectedKeys: string[] = this.selectedKeys();

    return (this.model()?.options ?? []).map(option => {
      const key: string = getSelectionListOptionKey(option);
      const toggleId: string = `${this.id}-${key}`;

      return {
        key,
        label: option.label,
        selected: selectedKeys.includes(key),
        description: option.description,
        toggleId,
        toggleInputId: `${toggleId}-input`,
      };
    });
  });

  public get data(): SelectionListInputModel {
    // the input is always bound before anything reads it back, both in templates and in dynamic forms
    return this.model() as SelectionListInputModel;
  }

  @Input()
  public set data(value: SelectionListInputModel) {
    this.model.set(value);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.syncSelection(this.control.value as SelectionListValue);
    this.syncDisabled();

    // dynamic forms write through the control instead of the value accessor
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.syncSelection(this.control.value as SelectionListValue));

    // dynamic forms disable the control instead of going through the value accessor
    this.control.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.syncDisabled());
  }

  public onSelectionChange(key: string, selected: boolean): void {
    const selectedKeys: string[] = this.toggleKey(key, selected);

    this.selectedKeys.set(selectedKeys);
    this.onChangeCallback(this.toControlValue(selectedKeys));

    if (!this.control.touched) {
      this.onTouchedCallback();
    }
  }

  public override writeValue(value: SelectionListValue): void {
    this.syncSelection(value);
  }

  public override registerOnChange(fn: (_: unknown) => unknown): void {
    this.onChangeCallback = fn;
  }

  public override registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public override setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private syncSelection(value: SelectionListValue): void {
    this.selectedKeys.set(this.toSelectedKeys(value));
  }

  private syncDisabled(): void {
    this.disabled.set(this.control.disabled);
  }

  /**
   * Checkboxes add to and remove from the selection, a radio button replaces it.
   */
  private toggleKey(key: string, selected: boolean): string[] {
    if (!this.multiple()) {
      return selected ? [key] : [];
    }

    const selectedKeys: string[] = this.selectedKeys().filter(selectedKey => selectedKey !== key);

    return selected ? [...selectedKeys, key] : selectedKeys;
  }

  /**
   * Bridges the control value onto the list: a single selected option for radio buttons,
   * every option flagged `true` for checkboxes.
   */
  private toSelectedKeys(value: SelectionListValue): string[] {
    if (typeof value === 'string') {
      return [value];
    }

    if (value === null || typeof value !== 'object') {
      return [];
    }

    return Object.keys(value).filter(key => value[key]);
  }

  /**
   * Bridges the list selection back onto the control value, keeping the shape each input type had
   * as a standalone component: the selected option value for radio buttons, a flag per option for checkboxes.
   */
  private toControlValue(selectedKeys: string[]): SelectionListValue {
    if (!this.multiple()) {
      return selectedKeys[0] ?? null;
    }

    return this.data.options.reduce<Record<string, boolean>>(
      (value, option) => {
        const key: string = getSelectionListOptionKey(option);

        return { ...value, [key]: selectedKeys.includes(key) };
      },
      {},
    );
  }
}
